export const VALID_PERIODS = ['7d', '30d', '90d'] as const;
export type ValidPeriod = typeof VALID_PERIODS[number];

export const VALID_EXPORT_TYPES = ['push', 'users', 'conversions'] as const;
export type ValidExportType = typeof VALID_EXPORT_TYPES[number];

export const VALID_GROUP_BY = ['day', 'week', 'month'] as const;
export type ValidGroupBy = typeof VALID_GROUP_BY[number];

export const VALID_OS = ['iOS', 'Android', 'Web', 'Windows', 'MacOS', 'Linux'] as const;
export type ValidOS = typeof VALID_OS[number];

export const VALID_PUSH_TYPES = ['regular', 'event', 'special'] as const;
export type ValidPushType = typeof VALID_PUSH_TYPES[number];

export const VALID_CONFIG_TYPES = ['single', 'campaign'] as const;
export type ValidConfigType = typeof VALID_CONFIG_TYPES[number];

export const VALID_PUSH_STATUS = ['scheduled', 'sending', 'sent', 'failed', 'cancelled'] as const;
export type ValidPushStatus = typeof VALID_PUSH_STATUS[number];

export const FCM_LIMITS = {
  MAX_TITLE_LENGTH: 65,
  MAX_BODY_LENGTH: 240,
  MAX_MESSAGE_SIZE: 4096,
  MAX_BATCH_SIZE: 500,
  MAX_TOKENS_PER_REQUEST: 1000,
} as const;

export const PAGINATION_LIMITS = {
  MIN_LIMIT: 1,
  MAX_LIMIT: 1000,
  DEFAULT_LIMIT: 20,
  MIN_OFFSET: 0,
} as const;

export const validators = {
  email: (email: string): boolean => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  },

  password: (password: string): boolean => {
    return password.length >= 8 && password.length <= 128;
  },

  strongPassword: (password: string): boolean => {
    const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,128}$/;
    return re.test(password);
  },

  bundleId: (bundleId: string): boolean => {
    const re = /^[a-zA-Z][a-zA-Z0-9\-._]+$/;
    return re.test(bundleId) && bundleId.length > 0 && bundleId.length <= 255;
  },

  appName: (name: string): boolean => {
    return name.trim().length >= 1 && name.length <= 255;
  },

  pushTitle: (title: string): boolean => {
    return title.trim().length > 0 && title.length <= FCM_LIMITS.MAX_TITLE_LENGTH;
  },

  pushBody: (body: string): boolean => {
    return body.trim().length > 0 && body.length <= FCM_LIMITS.MAX_BODY_LENGTH;
  },

  pushToken: (token: string): boolean => {
    return token.length >= 20 && token.length <= 4096;
  },

  pushMessageSize: (title: string, body: string, data?: Record<string, any>): boolean => {
    let totalSize = new Blob([title]).size + new Blob([body]).size;
    if (data) {
      totalSize += new Blob([JSON.stringify(data)]).size;
    }
    return totalSize <= FCM_LIMITS.MAX_MESSAGE_SIZE;
  },

  pushType: (type: string): type is ValidPushType => {
    return VALID_PUSH_TYPES.includes(type as ValidPushType);
  },

  configType: (type: string): type is ValidConfigType => {
    return VALID_CONFIG_TYPES.includes(type as ValidConfigType);
  },

  pushStatus: (status: string): status is ValidPushStatus => {
    return VALID_PUSH_STATUS.includes(status as ValidPushStatus);
  },

  campaignName: (name: string): boolean => {
    return name.trim().length >= 3 && name.length <= 100;
  },

  segmentName: (name: string): boolean => {
    return name.trim().length >= 3 && name.length <= 100;
  },

  segmentConditions: (conditions: any[]): boolean => {
    if (!Array.isArray(conditions) || conditions.length === 0) {
      return false;
    }

    return conditions.every(
      (cond) =>
        cond.field &&
        cond.operator &&
        cond.value !== undefined &&
        cond.value !== null
    );
  },

  locale: (locale: string): boolean => {
    const re = /^[a-z]{2}(-[A-Z]{2})?$/;
    return re.test(locale);
  },

  os: (os: string): boolean => {
    return VALID_OS.includes(os as ValidOS);
  },

  appsflyerId: (afId: string): boolean => {
    if (!afId) return true;
    const re = /^[a-zA-Z0-9-]+$/;
    return re.test(afId) && afId.length <= 255;
  },

  userExternalId: (userId: string): boolean => {
    return userId.trim().length > 0 && userId.length <= 255;
  },

  offer: (offer: string): boolean => {
    if (!offer) return true;
    return offer.trim().length > 0 && offer.length <= 255;
  },

  period: (period: string): period is ValidPeriod => {
    return VALID_PERIODS.includes(period as ValidPeriod);
  },

  exportType: (type: string): type is ValidExportType => {
    return VALID_EXPORT_TYPES.includes(type as ValidExportType);
  },

  groupBy: (groupBy: string): groupBy is ValidGroupBy => {
    return VALID_GROUP_BY.includes(groupBy as ValidGroupBy);
  },

  paginationLimit: (limit: number): boolean => {
    return (
      Number.isInteger(limit) &&
      limit >= PAGINATION_LIMITS.MIN_LIMIT &&
      limit <= PAGINATION_LIMITS.MAX_LIMIT
    );
  },

  paginationOffset: (offset: number): boolean => {
    return Number.isInteger(offset) && offset >= PAGINATION_LIMITS.MIN_OFFSET;
  },

  batchSize: (size: number): boolean => {
    return (
      Number.isInteger(size) &&
      size > 0 &&
      size <= FCM_LIMITS.MAX_BATCH_SIZE
    );
  },

  campaignIdsForComparison: (ids: number[]): boolean => {
    return Array.isArray(ids) && ids.length >= 2 && ids.length <= 5;
  },

  segmentIdsForComparison: (ids: number[]): boolean => {
    return Array.isArray(ids) && ids.length >= 2 && ids.length <= 5;
  },

  url: (url: string): boolean => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  },

  imageUrl: (url: string): boolean => {
    if (!url) return true;
    if (!validators.url(url)) return false;

    const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/i;
    return imageExtensions.test(url) && url.length <= 2048;
  },

  json: (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  },

  customData: (data: any): boolean => {
    if (!data) return true;

    try {
      const jsonString = JSON.stringify(data);
      return jsonString.length <= 4096;
    } catch {
      return false;
    }
  },

  firebaseConfig: (config: any): boolean => {
    const requiredFields = [
      'type',
      'project_id',
      'private_key_id',
      'private_key',
      'client_email',
      'client_id',
      'auth_uri',
      'token_uri',
    ];

    if (typeof config !== 'object' || config === null) return false;

    return requiredFields.every(
      (field) => config.hasOwnProperty(field) && config[field]
    );
  },

  dateString: (dateStr: string): boolean => {
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
  },

  dateRange: (startDate: string, endDate: string): boolean => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return !isNaN(start.getTime()) && !isNaN(end.getTime()) && start <= end;
  },

  futureDate: (date: string): boolean => {
    const d = new Date(date);
    return !isNaN(d.getTime()) && d > new Date();
  },

  pastDate: (date: string): boolean => {
    const d = new Date(date);
    return !isNaN(d.getTime()) && d < new Date();
  },

  scheduledDate: (date: string): boolean => {
    const d = new Date(date);
    const now = new Date();
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(now.getFullYear() + 1);

    return (
      !isNaN(d.getTime()) &&
      d > now &&
      d <= oneYearFromNow
    );
  },

  positiveNumber: (value: number): boolean => {
    return typeof value === 'number' && !isNaN(value) && value > 0;
  },

  nonNegativeNumber: (value: number): boolean => {
    return typeof value === 'number' && !isNaN(value) && value >= 0;
  },

  percentage: (value: number): boolean => {
    return typeof value === 'number' && !isNaN(value) && value >= 0 && value <= 100;
  },

  required: (value: any): boolean => {
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    if (typeof value === 'number') {
      return !isNaN(value);
    }
    return value !== null && value !== undefined;
  },

  minLength: (value: string, min: number): boolean => {
    return value.length >= min;
  },

  maxLength: (value: string, max: number): boolean => {
    return value.length <= max;
  },

  length: (value: string, min: number, max: number): boolean => {
    return value.length >= min && value.length <= max;
  },

  range: (value: number, min: number, max: number): boolean => {
    return value >= min && value <= max;
  },

  arrayNotEmpty: (arr: any[]): boolean => {
    return Array.isArray(arr) && arr.length > 0;
  },

  arrayLength: (arr: any[], min: number, max: number): boolean => {
    return Array.isArray(arr) && arr.length >= min && arr.length <= max;
  },

  phoneNumber: (phone: string): boolean => {
    const re = /^\+?[1-9]\d{1,14}$/;
    return re.test(phone.replace(/\s/g, ''));
  },

  title: (title: string): boolean => {
    return validators.campaignName(title);
  },

  body: (body: string): boolean => {
    return validators.pushBody(body);
  },
};

export const formRules = {
  email: [
    { required: true, message: 'Email обязателен для заполнения' },
    { type: 'email' as const, message: 'Пожалуйста, введите корректный email' },
    {
      validator: (_: any, value: string) => {
        if (!value || validators.email(value)) {
          return Promise.resolve();
        }
        return Promise.reject('Неверный формат email');
      },
    },
  ],

  password: [
    { required: true, message: 'Пароль обязателен для заполнения' },
    { min: 8, message: 'Пароль должен быть не короче 8 символов' },
    { max: 128, message: 'Пароль не должен превышать 128 символов' },
  ],

  strongPassword: [
    { required: true, message: 'Пароль обязателен для заполнения' },
    {
      validator: (_: any, value: string) => {
        if (!value) return Promise.reject();
        if (!validators.strongPassword(value)) {
          return Promise.reject(
            'Пароль должен содержать заглавные и строчные буквы, а также цифры'
          );
        }
        return Promise.resolve();
      },
    },
  ],

  confirmPassword: [
    { required: true, message: 'Подтвердите свой пароль' },
    ({ getFieldValue }: any) => ({
      validator(_: any, value: string) {
        if (!value || getFieldValue('password') === value) {
          return Promise.resolve();
        }
        return Promise.reject('Пароли не сошлись');
      },
    }),
  ],

  bundleId: [
    { required: true, message: 'Bundle ID обязателен' },
    {
      validator: (_: any, value: string) => {
        if (!value) return Promise.resolve();
        if (!validators.bundleId(value)) {
          return Promise.reject(
            'Bundle ID должен начинаться с буквы и содержать только буквы, цифры, точки, дефисы и подчёркивания'
          );
        }
        return Promise.resolve();
      },
    },
  ],

  appName: [
    { required: true, message: 'Название приложения обязательно' },
    { max: 255, message: 'Название не должно превышать 255 символов' },
  ],

  pushTitle: [
    { required: true, message: 'Заголовок обязателен' },
    { max: FCM_LIMITS.MAX_TITLE_LENGTH, message: `Заголовок не должен превышать ${FCM_LIMITS.MAX_TITLE_LENGTH} символов` },
    {
      validator: (_: any, value: string) => {
        if (value && /\d/.test(value)) {
          return Promise.reject('Без точных цифр, чтобы не вводить в заблуждение пользователя');
        }
        return Promise.resolve();
      }
    }
  ],

  pushBody: [
    { required: true, message: 'Текст обязателен' },
    { max: FCM_LIMITS.MAX_BODY_LENGTH, message: `Текст не должен превышать ${FCM_LIMITS.MAX_BODY_LENGTH} символов` },
    {
      validator: (_: any, value: string) => {
        if (value && /\d/.test(value)) {
          return Promise.reject('И не указывать конкретные бонусы с цифрами');
        }
        return Promise.resolve();
      }
    }
  ],

  pushToken: [
    { required: true, message: 'Push-токен обязателен' },
    {
      validator: (_: any, value: string) => {
        if (!value) return Promise.resolve();
        if (!validators.pushToken(value)) {
          return Promise.reject('Неверный формат push-токена');
        }
        return Promise.resolve();
      },
    },
  ],

  campaignName: [
    { required: true, message: 'Название кампании обязательно' },
    { min: 3, message: 'Название кампании должно содержать минимум 3 символа' },
    { max: 100, message: 'Название кампании не должно превышать 100 символов' },
  ],

  segmentName: [
    { required: true, message: 'Название сегмента обязательно' },
    { min: 3, message: 'Название сегмента должно содержать минимум 3 символа' },
    { max: 100, message: 'Название сегмента не должно превышать 100 символов' },
  ],

  campaignDescription: [
    { max: 500, message: 'Описание не должно превышать 500 символов' },
  ],

  locale: [
    {
      validator: (_: any, value: string) => {
        if (!value || validators.locale(value)) {
          return Promise.resolve();
        }
        return Promise.reject('Локаль должна быть в формате: en или en-US');
      },
    },
  ],

  os: [
    { required: true, message: 'ОС обязательна' },
    {
      validator: (_: any, value: string) => {
        if (!value || validators.os(value)) {
          return Promise.resolve();
        }
        return Promise.reject(`ОС должна быть одной из: ${VALID_OS.join(', ')}`);
      },
    },
  ],

  url: [
    { type: 'url' as const, message: 'Пожалуйста, введите корректный URL' },
  ],

  imageUrl: [
    {
      validator: (_: any, value: string) => {
        if (!value) return Promise.resolve();
        if (!validators.imageUrl(value)) {
          return Promise.reject('Пожалуйста, введите корректный URL изображения (jpg, png, gif, webp, svg)');
        }
        return Promise.resolve();
      },
    },
  ],

  json: [
    {
      validator: (_: any, value: string) => {
        if (!value) return Promise.resolve();
        if (!validators.json(value)) {
          return Promise.reject('Пожалуйста, введите корректный JSON');
        }
        return Promise.resolve();
      },
    },
  ],

  customData: [
    {
      validator: (_: any, value: any) => {
        if (!value) return Promise.resolve();
        if (!validators.customData(value)) {
          return Promise.reject('Дополнительные данные превышают максимальный размер в 4KB');
        }
        return Promise.resolve();
      },
    },
  ],

  firebaseConfig: [
    { required: true, message: 'Конфигурация Firebase обязательна' },
    {
      validator: (_: any, value: any) => {
        if (!value) return Promise.reject();
        if (!validators.firebaseConfig(value)) {
          return Promise.reject('Неверная конфигурация Firebase. Проверьте все обязательные поля.');
        }
        return Promise.resolve();
      },
    },
  ],

  scheduledDate: [
    {
      validator: (_: any, value: string) => {
        if (!value) return Promise.resolve();
        if (!validators.scheduledDate(value)) {
          return Promise.reject('Дата должна быть в будущем, но не позднее чем через 1 год');
        }
        return Promise.resolve();
      },
    },
  ],

  dateRange: [
    {
      validator: (_: any, value: [string, string]) => {
        if (!value || !value[0] || !value[1]) return Promise.resolve();
        if (!validators.dateRange(value[0], value[1])) {
          return Promise.reject('Дата начала должна быть раньше даты окончания');
        }
        return Promise.resolve();
      },
    },
  ],

  positiveNumber: [
    { required: true, message: 'Это поле обязательно' },
    { type: 'number' as const, message: 'Должно быть числом' },
    {
      validator: (_: any, value: number) => {
        if (value === undefined || value === null) return Promise.resolve();
        if (!validators.positiveNumber(value)) {
          return Promise.reject('Должно быть положительным числом');
        }
        return Promise.resolve();
      },
    },
  ],

  nonNegativeNumber: [
    { required: true, message: 'Это поле обязательно' },
    { type: 'number' as const, message: 'Должно быть числом' },
    {
      validator: (_: any, value: number) => {
        if (value === undefined || value === null) return Promise.resolve();
        if (!validators.nonNegativeNumber(value)) {
          return Promise.reject('Не может быть отрицательным числом');
        }
        return Promise.resolve();
      },
    },
  ],

  percentage: [
    { type: 'number' as const, message: 'Должно быть числом' },
    {
      validator: (_: any, value: number) => {
        if (value === undefined || value === null) return Promise.resolve();
        if (!validators.percentage(value)) {
          return Promise.reject('Должно быть от 0 до 100');
        }
        return Promise.resolve();
      },
    },
  ],

  paginationLimit: [
    { type: 'number' as const, message: 'Должно быть числом' },
    {
      validator: (_: any, value: number) => {
        if (value === undefined || value === null) return Promise.resolve();
        if (!validators.paginationLimit(value)) {
          return Promise.reject(
            `Должно быть от ${PAGINATION_LIMITS.MIN_LIMIT} до ${PAGINATION_LIMITS.MAX_LIMIT}`
          );
        }
        return Promise.resolve();
      },
    },
  ],

  required: (fieldName: string) => [
    { required: true, message: `${fieldName} обязательно для заполнения` },
  ],

  minLength: (min: number) => [
    { min, message: `Должно содержать минимум ${min} символов` },
  ],

  maxLength: (max: number) => [
    { max, message: `Не должно превышать ${max} символов` },
  ],

  range: (min: number, max: number) => [
    { min, message: `Должно быть не меньше ${min}` },
    { max, message: `Не должно превышать ${max}` },
  ],

  title: [
    { required: true, message: 'Заголовок обязателен' },
    { min: 3, message: 'Заголовок должен содержать минимум 3 символа' },
    { max: 100, message: 'Заголовок не должен превышать 100 символов' },
  ],

  body: [
    { required: true, message: 'Текст обязателен' },
    { min: 10, message: 'Текст должен содержать минимум 10 символов' },
    { max: 500, message: 'Текст не должен превышать 500 символов' },
  ],
};

export const validationMessages = {
  required: (field: string) => `Поле "${field}" обязательно для заполнения`,

  minLength: (field: string, min: number) => `Поле "${field}" должно содержать минимум ${min} символов`,
  maxLength: (field: string, max: number) => `Поле "${field}" не должно превышать ${max} символов`,
  exactLength: (field: string, length: number) => `Поле "${field}" должно содержать ровно ${length} символов`,

  minValue: (field: string, min: number) => `Значение "${field}" должно быть не меньше ${min}`,
  maxValue: (field: string, max: number) => `Значение "${field}" не должно превышать ${max}`,
  positive: (field: string) => `Значение "${field}" должно быть положительным числом`,
  nonNegative: (field: string) => `Значение "${field}" не может быть отрицательным`,

  invalidFormat: (field: string) => `Неверный формат поля "${field}"`,
  invalidEmail: 'Неверный адрес электронной почты',
  invalidUrl: 'Неверный URL-адрес',
  invalidJson: 'Неверный формат JSON',
  invalidDate: 'Неверная дата',

  passwordTooWeak: 'Пароль должен содержать заглавные и строчные буквы, а также цифры',
  passwordsNotMatch: 'Пароли не совпадают',
  dateInPast: 'Дата должна быть в будущем',
  dateInFuture: 'Дата должна быть в прошлом',
  invalidDateRange: 'Дата начала должна быть раньше даты окончания',

  pushTitleTooLong: `Заголовок не должен превышать ${FCM_LIMITS.MAX_TITLE_LENGTH} символов`,
  pushBodyTooLong: `Текст не должен превышать ${FCM_LIMITS.MAX_BODY_LENGTH} символов`,
  pushMessageTooLarge: 'Размер сообщения превышает лимит в 4KB',

  notEnoughItemsForComparison: 'Выберите как минимум 2 элемента для сравнения',
  tooManyItemsForComparison: 'Нельзя сравнить более 5 элементов',
};

export const validateFields = <T extends Record<string, any>>(
  data: T,
  rules: Partial<Record<keyof T, (value: any) => boolean>>
): Partial<Record<keyof T, string>> => {
  const errors: Partial<Record<keyof T, string>> = {};

  for (const [field, validator] of Object.entries(rules)) {
    const value = data[field as keyof T];
    if (validator && !validator(value)) {
      errors[field as keyof T] = validationMessages.invalidFormat(field);
    }
  }

  return errors;
};

export const sanitizeForCSV = (value: string): string => {
  if (typeof value !== 'string') {
    return String(value);
  }

  if (/[,"\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

export const sanitizeCSVRow = (values: any[]): string => {
  return values.map((v) => sanitizeForCSV(String(v ?? ''))).join(',');
};

export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

export const validateNested = (
  data: any,
  schema: Record<string, (value: any) => boolean>
): Record<string, string> => {
  const errors: Record<string, string> = {};

  const validate = (obj: any, path: string = '') => {
    for (const [key, value] of Object.entries(obj)) {
      const fullPath = path ? `${path}.${key}` : key;
      const validator = schema[fullPath];

      if (validator && !validator(value)) {
        errors[fullPath] = validationMessages.invalidFormat(fullPath);
      }

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        validate(value, fullPath);
      }
    }
  };

  validate(data);
  return errors;
};

export default validators;